import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usersAPI } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { Eye, Search, X } from 'lucide-react';
import { toast } from 'sonner';

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  lider_proyecto: 'Líder de Proyecto',
  levantador: 'Levantador',
  cliente: 'Cliente',
};

export default function MirrorModePage() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const { mirrorUser, setMirrorUser, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    usersAPI.list().then((r) => {
      setUsers((r.data.items || []).filter((u) => u.id !== user?.id));
    }).catch(() => toast.error('Error al cargar usuarios')).finally(() => setLoading(false));
  }, [user]);

  const filtered = users.filter((u) => {
    const q = search.toLowerCase();
    return u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
  });

  const activateMirror = (u) => {
    // Activación de modo espejo sin race conditions (#yabzzxVc).
    //
    // Intento 1 (original): navigate('/cliente') primero, después
    // setMirrorUser. Bug: activeRole seguía super_admin al chequear
    // /cliente → access-denied.
    // Intento 2: setMirrorUser primero, después navigate. Bug:
    // activeRole pasaba a lider/levantador mientras el usuario estaba
    // todavía en /dashboard/modo-espejo (requires super_admin) →
    // access-denied antes de que el navigate corriera.
    //
    // Fix definitivo: persistir el mirror en localStorage y forzar
    // una recarga completa del SPA con window.location.replace. En el
    // primer render del nuevo bundle, AuthProvider lee el mirrorUser
    // de localStorage y activeRole ya resuelve al rol espejo desde
    // el inicio. Cero ventana para que un guard intermedio dispare.
    setMirrorUser(u);
    toast.success(`Modo Espejo activado: ${u.name}`);
    const target = u.role === 'cliente' ? '/cliente' : '/dashboard';
    window.location.replace(target);
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="mirror-mode-page">
      <div>
        <h1 className="text-2xl font-bold text-soem-navy">Modo Espejo</h1>
        <p className="text-slate-500 text-sm mt-1">
          Visualiza la plataforma desde la perspectiva de cualquier usuario.
        </p>
      </div>

      {mirrorUser && (
        <div className="bg-yellow-50 border border-yellow-300 rounded-sm p-4 flex items-center justify-between" data-testid="mirror-active-banner">
          <div className="flex items-center gap-3">
            <Eye size={20} className="text-yellow-600" />
            <div>
              <p className="font-semibold text-yellow-800">Modo Espejo activo</p>
              <p className="text-yellow-600 text-sm">Viendo como: <strong>{mirrorUser.name}</strong> ({ROLE_LABELS[mirrorUser.role]})</p>
            </div>
          </div>
          <button onClick={() => {
              // #yabzzxVc — mismo race que el botón Salir del Layout.
              // Si el espejo era cliente y estamos viendo /cliente,
              // setMirrorUser(null) sin recarga dispara access-denied.
              setMirrorUser(null);
              toast.success('Modo Espejo desactivado');
              window.location.replace('/dashboard');
            }}
            className="soem-btn-secondary px-3 py-1.5 text-sm flex items-center gap-2" data-testid="deactivate-mirror-btn">
            <X size={14} /> Desactivar
          </button>
        </div>
      )}

      <div className="soem-card">
        <div className="p-5 border-b border-slate-100">
          <div className="relative max-w-sm">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              className="pl-9 pr-4 py-2 border border-slate-300 rounded-sm text-sm w-full focus:outline-none focus:ring-2 focus:ring-soem-primary"
              placeholder="Buscar por nombre o email..." aria-label="Buscar usuario"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="mirror-search-input"
            />
          </div>
        </div>
        <div className="divide-y divide-slate-50">
          {loading ? (
            [...Array(4)].map((_, i) => (
              <div key={i} className="p-5"><div className="h-4 bg-slate-100 rounded animate-pulse" /></div>
            ))
          ) : filtered.length === 0 ? (
            <p className="text-center text-slate-400 py-8">No se encontraron usuarios.</p>
          ) : (
            filtered.map((u) => (
              <div key={u.id} className="flex items-center justify-between px-5 py-4 hover:bg-blue-50/40 transition-colors" data-testid={`mirror-user-${u.id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-soem-primary rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-bold">{u.name.charAt(0)}</span>
                  </div>
                  <div>
                    <p className="font-semibold text-soem-navy">{u.name}</p>
                    <p className="text-slate-400 text-xs">{u.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="bg-blue-50 text-blue-700 text-xs px-2 py-0.5 rounded-full font-medium">
                    {ROLE_LABELS[u.role]}
                  </span>
                  <button
                    onClick={() => activateMirror(u)}
                    className="soem-btn-primary px-3 py-1.5 text-xs flex items-center gap-1.5"
                    disabled={u.is_active === false || u.status === 'inactivo' || u.status === 'suspendido'}
                    data-testid={`activate-mirror-${u.id}`}
                  >
                    <Eye size={13} /> Ver como este usuario
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
